import { useEffect, useState } from 'react';
import { api, type ApiError } from '../api/client';
import { Alert } from '../components/Field';

interface RoleRow {
  id: string;
  code: string;
  name: string;
  scope: string;
  isSystem: boolean;
  assignedUsers: number;
  permissions: string[];
}

export function Roles() {
  const [roles, setRoles] = useState<RoleRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    api<{ items: RoleRow[] }>('/admin/roles')
      .then((result) => setRoles(result.items))
      .catch((caught) => setError(caught as ApiError));
  }, []);

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Roles &amp; permissions</h1>
        <p className="page__lede">
          System roles are seeded from the BRD role matrix and cannot be deleted. Separation of
          duties is deliberate: finance cannot reach content, and content cannot reach payments.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} /> : null}

      <div className="card">
        {!roles ? (
          <div className="spinner">Loading roles…</div>
        ) : (
          // Fixed layout with set widths, so opening a role's permission list
          // wraps inside its column instead of resizing every other column.
          <table className="grid" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '24%' }} />
              <col style={{ width: '11%' }} />
              <col />
              <col style={{ width: '9%' }} />
              <col style={{ width: '120px' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Role</th>
                <th>Type</th>
                <th>Permissions</th>
                <th>Assigned</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td>
                    <strong>{role.name}</strong>
                    <br />
                    <span className="muted" style={{ fontSize: 12 }}>
                      {role.code}
                    </span>
                  </td>
                  <td>
                    {/* The API's scope says where the role is defined, not where
                        it applies: PLATFORM roles are the built-in set every
                        organization shares, TENANT ones an organization made. */}
                    {role.scope === 'PLATFORM' ? (
                      <span
                        className="pill pill--role"
                        title="Defined by SkillXzen and shared by every organization"
                      >
                        Built-in
                      </span>
                    ) : (
                      <span className="pill pill--inactive" title="Created by one organization">
                        Custom
                      </span>
                    )}
                  </td>
                  <td>
                    {open === role.id ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {role.permissions.map((permission) => (
                          <span className="pill pill--inactive" key={permission}>
                            {permission}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="muted">{role.permissions.length} permissions</span>
                    )}
                  </td>
                  <td>{role.assignedUsers}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() => setOpen(open === role.id ? null : role.id)}
                    >
                      {open === role.id ? 'Hide' : 'Show'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
